import React, { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { menuItems } from "../../constants/data";
import i18n from "../../i18n";
import { handleLogout } from "../../utils/data";
import ProfileMenu from "./ProfileMenu";
import { CiEdit } from "react-icons/ci";
import { startEditing, updateUser } from "../../features/auth/userSlice";
import { useTranslation } from "react-i18next";
import { useSEO } from "../../custom hooks/useSEO";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

const ProfileLayout = () => {
        const { t: tSeo} = useTranslation("seo");
    
      const seo=useSEO({
        title: tSeo("profile.title"),
        description: tSeo("profile.description")
      });
    const dispatch = useDispatch<AppDispatch>();
    const { user, isEditing } = useSelector((state: RootState) => state.user);
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(menuItems[0]);

    const handleSelect = (item: any) => {
        setSelectedOption(item);
        setDrawerOpen(false);
        if (item.link === "logout") {
            handleLogout();
        } else {
            navigate(`/me${item.link}`);
        }
    };
  const prepareUserFormData = (file?: File) => {
        const formDataToSend = new FormData();
        formDataToSend.append("id", String(user?.id || ""));
        formDataToSend.append("name", user?.name || "");
        formDataToSend.append("location", user?.location || "");
        formDataToSend.append("currencyCode", user?.currencyCode || "");
        formDataToSend.append("latitude", user?.latitude?.toString() || "");
        formDataToSend.append("longitude", user?.longitude?.toString() || "");
        if (file) {
            formDataToSend.append("image", file);
        }
        return formDataToSend;
    };
    return (
        <div className="bg-white">
            {seo}


            <div className="padding-container main-prop">
                <div className="grid xs:grid-cols-1 md:grid-cols-4 gap-5">
                    <div className="col-span-1">
                        {/* Mobile Menu Trigger */}
                        <div
                            className="w-full xs:block md:hidden mb-5 border border-primary text-primary rounded-xl overflow-hidden p-3 cursor-pointer"
                            onClick={() => setDrawerOpen(true)}
                        >
                            <p className="text-sm">
                                {i18n.language === "ar"
                                    ? selectedOption.title_ar
                                    : selectedOption.title_en}
                            </p>
                        </div>

                        {/* Drawer */}
                        <div
                            className={`fixed top-0 left-0 h-full w-64 bg-white shadow-xl transform transition-transform duration-300 z-50 ${drawerOpen ? "translate-x-0" : "-translate-x-full"
                                }`}
                        >
                            <div className="p-4 flex justify-between items-center border-b">
                                <h2 className="font-semibold">{t("menu")}</h2>
                                <button onClick={() => setDrawerOpen(false)}>✕</button>
                            </div>
                            <ul className="p-4 space-y-3">
                                {menuItems.map((item, idx) => (
                                    <li
                                        key={idx}
                                        onClick={() => handleSelect(item)}
                                        className="cursor-pointer text-textSecond hover:text-primary transition"
                                    >
                                        {i18n.language === "ar" ? item.title_ar : item.title_en}
                                    </li>
                                ))}
                                <li
                                    onClick={() => handleSelect({ link: "logout" })}
                                    className="cursor-pointer text-red-600 hover:text-red-700 transition"
                                >
                                    {t("logout")}
                                </li>
                            </ul>
                        </div>

                        {/* Overlay */}
                        {drawerOpen && (
                            <div
                                className="fixed inset-0 bg-black bg-opacity-40 z-40"
                                onClick={() => setDrawerOpen(false)}
                            ></div>
                        )}

                        {/* Profile Header */}
                        <div className={`flex border ${user?.phone==="+99900000000"&&"border-red-600 bg-[#dc26261f]"} w-full rounded-xl overflow-hidden items-center gap-3 mb-4 p-4`}>
                            <div className="relative">
                                <div className="relative w-14 h-14">
                                    <img
                                        src={`${user?.image ? user.image : "/asset/user-profile-icon.jpg"}`}
                                        alt="Profile"
                                        className="w-full h-full overflow-hidden rounded-full object-cover"
                                    />

                                    {/* Hidden File Input */}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        id="profileImageInput"
                                        className="hidden"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                const file = e.target.files[0];
                                                const formData = prepareUserFormData(file);
                                                dispatch(updateUser(formData))
                                            }
                                        }}
                                    />

                                    {isEditing && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                document.getElementById("profileImageInput")?.click();
                                            }}
                                            className="absolute -bottom-2 right-1/2 translate-x-1/2 bg-white border rounded-full p-1 shadow-md hover:bg-gray-100"
                                        >
                                            <CiEdit className="text-secondry" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <h2 className="font-semibold line-clamp-2">
                                    {t("hi")}, {user?.name} 👋
                                </h2>
                                {user?.phone==="+99900000000"?
                                <Link to={"/me/add-number"} className="text-sm py-1 text-red-600 flex gap-1 items-center animate-pulse">{t("phone-number-required")}  {i18n.language==="ar"?<FaArrowLeft />:<FaArrowRight />} </Link>:
                                <p className=" text-textSecond text-sm ">{user?.phone}</p>}
                                {!isEditing && (
                                    <button
                                        onClick={() => dispatch(startEditing())}
                                        className="text-sm text-secondry"
                                    >
                                        {t("editProfile")}
                                    </button>
                                )}

                            </div>
                        </div>

                        <div className="xs:hidden md:block">
                            <ProfileMenu />
                        </div>
                    </div>

                    <div className="xs:grid-cols-1 md:col-span-3">
                        <Outlet />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileLayout;
